import { createContext, useContext, useState, useEffect, useCallback } from "react";

const CartContext = createContext();

const API = `${process.env.REACT_APP_API_URL || "http://localhost:5001"}/api/cart`;

export const CartProvider = ({ children }) => {
  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [token,   setToken]   = useState(() => localStorage.getItem("sf_token"));

  // ── Listen for auth changes (login / logout) ────────────────────────────
  useEffect(() => {
    const sync = () => setToken(localStorage.getItem("sf_token"));
    window.addEventListener("storage",        sync);
    window.addEventListener("sf:auth-change", sync);
    return () => {
      window.removeEventListener("storage",        sync);
      window.removeEventListener("sf:auth-change", sync);
    };
  }, []);

  // ── Fetch cart whenever token changes ───────────────────────────────────
  const fetchCart = useCallback(async () => {
    const t = localStorage.getItem("sf_token");
    if (!t) return;
    try {
      setLoading(true);
      const res = await fetch(API, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      setItems(data.items || []);
    } catch (err) {
      console.error("fetchCart:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) {
      fetchCart();
    } else {
      setItems([]); // clear cart on logout
    }
  }, [token, fetchCart]);

  // ── Add to cart ─────────────────────────────────────────────────────────
  // Instead of alert(), fires the global auth event so the Navbar modal opens.
  const addToCart = useCallback(async (product_id, quantity = 1, color = null, size = null) => {
  const t = localStorage.getItem("sf_token");
  if (!t) {
    // Return a promise that will be resolved after authentication and re‑call
    return new Promise((resolve, reject) => {
      const onAuthSuccess = async () => {
        try {
          // Re‑attempt add after login
          await addToCart(product_id, quantity, color, size);
          resolve();
        } catch (err) {
          reject(err);
        }
      };
      window.dispatchEvent(new CustomEvent("sf:require-auth", {
        detail: { onSuccess: onAuthSuccess },
      }));
    });
  }

  try {
    const res = await fetch(`${API}/add`, {
      method:  "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
      body:    JSON.stringify({ product_id, quantity, color, size }),
    });
    const data = await res.json();
    if (data.success) {
      await fetchCart(); // ✅ wait for cart update
    }
  } catch (err) {
    console.error("addToCart:", err);
    throw err;
  }
}, [fetchCart]);

  // ── Remove item ─────────────────────────────────────────────────────────
  const removeItem = useCallback(async (product_id, color = null, size = null) => {
    const t = localStorage.getItem("sf_token");
    if (!t) return;
    try {
      const res = await fetch(`${API}/remove`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
        body:    JSON.stringify({ product_id, color, size }),
      });
      const data = await res.json();
      if (data.success) fetchCart();
    } catch (err) {
      console.error("removeItem:", err);
    }
  }, [fetchCart]);

  // ── Update quantity ─────────────────────────────────────────────────────
  const updateQty = useCallback(async (product_id, quantity, color = null, size = null) => {
    const t = localStorage.getItem("sf_token");
    if (!t) return;
    try {
      const res = await fetch(`${API}/update`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
        body:    JSON.stringify({ product_id, quantity, color, size }),
      });
      const data = await res.json();
      if (data.success) fetchCart();
    } catch (err) {
      console.error("updateQty:", err);
    }
  }, [fetchCart]);

  // ── Clear cart ──────────────────────────────────────────────────────────
  const clearCart = useCallback(async () => {
    const t = localStorage.getItem("sf_token");
    if (!t) return;
    try {
      const res = await fetch(`${API}/clear`, {
        method:  "POST",
        headers: { Authorization: `Bearer ${t}` },
      });
      const data = await res.json();
      if (data.success) setItems([]);
    } catch (err) {
      console.error("clearCart:", err);
    }
  }, []);

  // ── Helpers ─────────────────────────────────────────────────────────────
  const isInCart = useCallback(
    (id, color = null, size = null) => items.some(item =>
      (item.product_id === id || item.id === id) &&
      (color === null || item.selectedColor === color) &&
      (size  === null || item.selectedSize  === size)
    ),
    [items]
  );

  const getQuantity = useCallback(
    (id, color = null, size = null) => {
      const item = items.find(i =>
        (i.product_id === id || i.id === id) &&
        (color === null || i.selectedColor === color) &&
        (size  === null || i.selectedSize  === size)
      );
      return item ? (item.quantity || item.qty || 0) : 0;
    },
    [items]
  );

  const totalItems = items.reduce((sum, i) => sum + (i.quantity || i.qty || 0), 0);
const totalPrice = items.reduce((sum, i) => {
  const price = parseFloat(i.effective_price ?? i.sale_price ?? i.price ?? 0);
  const qty   = i.quantity || i.qty || 0;
  return sum + price * qty;
}, 0);
  return (
    <CartContext.Provider value={{
      items,
      loading,
      addToCart,
      removeItem,
      updateQty,
      updateQuantity: updateQty, // alias for components using the old name
      clearCart,
      fetchCart,
      isInCart,
      getQuantity,
      totalItems,
      totalPrice,
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);